import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  IconButton,
  Modal,
} from '@mui/material';
import InstagramIcon from '@mui/icons-material/Instagram';
import CloseIcon from '@mui/icons-material/Close';
import PrivacyModal from '../Legal/PrivacyModal';

const anchorLinks = [
  { label: 'Servicios', href: '#services' },
  { label: 'Galería', href: '#gallery' },
  { label: 'Eventos', href: '#events' },
  { label: 'Opiniones', href: '#testimonials' },
  { label: 'FAQ', href: '#faq' },
];

const modalSx = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '95vw', sm: '80vw', md: '800px' },
  maxHeight: '85vh',
  overflowY: 'auto' as const,
  background: '#141210',
  border: '1px solid var(--color-border-hover)',
  borderRadius: 2,
  p: { xs: 3, md: 5 },
  outline: 'none',
};

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="h6"
    sx={{ fontFamily: 'var(--font-heading)', color: 'var(--color-accent)', mb: 1.5, mt: 3, fontSize: '1.05rem' }}
  >
    {children}
  </Typography>
);

const Para: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body2" sx={{ color: 'var(--color-text-secondary)', lineHeight: 1.9, mb: 1.2 }}>
    {children}
  </Typography>
);

const BulletList: React.FC<{ items: string[] }> = ({ items }) => (
  <Box component="ul" sx={{ pl: 2.5, mb: 1.2 }}>
    {items.map((item) => (
      <Typography key={item} component="li" variant="body2" sx={{ color: 'var(--color-text-secondary)', lineHeight: 1.9 }}>
        {item}
      </Typography>
    ))}
  </Box>
);

const LegalModal: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => (
  <Modal open={open} onClose={onClose}>
    <Box sx={modalSx}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Typography variant="h5" sx={{ fontFamily: 'var(--font-heading)', color: 'var(--color-text-primary)' }}>
          Aviso Legal
        </Typography>
        <IconButton onClick={onClose} sx={{ color: 'var(--color-text-secondary)', mt: -0.5 }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Para>Última actualización: mayo de 2026</Para>

      <SectionTitle>1. Datos identificativos</SectionTitle>
      <Para>
        En cumplimiento de la obligación de información establecida en el{' '}
        <strong style={{ color: 'var(--color-text-primary)' }}>
          artículo 10 de la Ley 34/2002, de 11 de julio, de Servicios de la Sociedad de la Información y de
          Comercio Electrónico (LSSI-CE)
        </strong>
        , se informa de los datos identificativos del titular de este sitio web:
      </Para>
      <BulletList items={[
        'Denominación social: Terrasuyi, S.L.',
        'CIF: B-88027701',
        'Domicilio social: C/ Juan Carlos I, n.º 13, local 19, 28806 Alcalá de Henares, Madrid, España',
        'Correo electrónico: sushigi.catering@gmail.com',
        'Dominio web: www.sushigi-catering.com',
      ]} />

      <SectionTitle>2. Objeto y aceptación</SectionTitle>
      <Para>
        El presente Aviso Legal regula el acceso y uso del sitio web{' '}
        <strong style={{ color: 'var(--color-text-primary)' }}>www.sushigi-catering.com</strong>, titularidad de
        Terrasuyi, S.L. El acceso al sitio web implica la aceptación plena y sin reservas de las presentes
        condiciones de uso.
      </Para>

      <SectionTitle>3. Propiedad intelectual e industrial</SectionTitle>
      <Para>
        Todos los contenidos del sitio web (textos, imágenes, diseño, logotipos, código fuente y demás elementos)
        son titularidad de Terrasuyi, S.L. o de terceros que han autorizado su uso, y están protegidos por las
        leyes de propiedad intelectual e industrial vigentes. Queda prohibida su reproducción, distribución o
        comunicación pública sin autorización expresa del titular.
      </Para>

      <SectionTitle>4. Responsabilidad</SectionTitle>
      <Para>
        Terrasuyi, S.L. no garantiza la continuidad ni la disponibilidad del sitio web ni de sus contenidos, y
        no se responsabiliza de los daños o perjuicios derivados del uso o imposibilidad de uso del sitio, de
        errores en los contenidos, ni de la presencia de virus u otros elementos lesivos en los archivos puestos
        a disposición del usuario.
      </Para>

      <SectionTitle>5. Ley aplicable y jurisdicción</SectionTitle>
      <Para>
        Este Aviso Legal se rige por la legislación española. Para la resolución de cualquier controversia
        derivada del acceso o uso del sitio web, las partes se someten a la jurisdicción de los Juzgados y
        Tribunales de Alcalá de Henares, con renuncia expresa a cualquier otro fuero que pudiera corresponderles.
      </Para>
    </Box>
  </Modal>
);

interface Props {
  onOpenContact: () => void;
}

const navLinkSx = {
  display: 'block',
  color: 'var(--color-text-secondary)',
  mb: 1,
  textDecoration: 'none',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  p: 0,
  fontFamily: 'inherit',
  fontSize: '0.875rem',
  textAlign: 'left' as const,
  transition: 'color 0.2s',
  '&:hover': { color: 'var(--color-accent)' },
};

const Footer: React.FC<Props> = ({ onOpenContact }) => {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [legalOpen, setLegalOpen] = useState(false);

  return (
    <>
      <Box component="footer" sx={{ py: 7, background: '#080808', borderTop: '1px solid var(--color-border)' }}>
        <Container maxWidth="lg">
          <Grid container spacing={5} sx={{ mb: 5 }}>

            {/* Brand column */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography
                variant="h6"
                sx={{ fontFamily: 'var(--font-heading)', color: 'var(--color-accent)', mb: 1.5, fontSize: '1.15rem', letterSpacing: '0.02em' }}
              >
                Sushigi Catering
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'var(--color-text-secondary)', lineHeight: 1.9, mb: 2.5, maxWidth: 280 }}
              >
                Gastronomía japonesa contemporánea para eventos únicos. Sushi premium, sushiman en vivo y experiencias a medida.
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton
                  component="a"
                  href="https://www.instagram.com/_sushigi_/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram de Sushigi Catering"
                  sx={{
                    color: 'var(--color-text-secondary)',
                    p: 1,
                    transition: 'color 0.2s, transform 0.2s',
                    '&:hover': { color: 'var(--color-accent)', transform: 'translateY(-2px)', background: 'transparent' },
                  }}
                >
                  <InstagramIcon sx={{ fontSize: 24 }} />
                </IconButton>
                <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)' }} href="https://www.instagram.com/_sushigi_/" component="a" target="_blank" rel="noopener noreferrer">
                  @_sushigi_
                </Typography>
              </Box>
            </Grid>

            {/* Navigation column */}
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography
                variant="subtitle2"
                sx={{ color: 'var(--color-text-primary)', mb: 2, letterSpacing: '0.12em', textTransform: 'uppercase', fontSize: '0.72rem' }}
              >
                Navegación
              </Typography>
              {anchorLinks.map((link) => (
                <Typography key={link.label} component="a" href={link.href} variant="body2" sx={navLinkSx}>
                  {link.label}
                </Typography>
              ))}
              <Typography component="button" variant="body2" onClick={onOpenContact} sx={navLinkSx}>
                Contacto
              </Typography>
            </Grid>

            {/* Contact column */}
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography
                variant="subtitle2"
                sx={{ color: 'var(--color-text-primary)', mb: 2, letterSpacing: '0.12em', textTransform: 'uppercase', fontSize: '0.72rem' }}
              >
                Contacto
              </Typography>
              <Typography
                component="a"
                href="mailto:sushigi.catering@gmail.com"
                variant="body2"
                sx={{ ...navLinkSx, display: 'block' }}
              >
                sushigi.catering@gmail.com
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--color-text-secondary)', lineHeight: 1.8 }}>
                C/ Juan Carlos I, n.º 13, local 19
                <br />
                28806 Alcalá de Henares, Madrid
              </Typography>
            </Grid>

          </Grid>

          {/* Bottom bar */}
          <Box
            sx={{
              borderTop: '1px solid var(--color-border)',
              pt: 3,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1.5,
            }}
          >
            <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)' }}>
              © 2026 Terrasuyi, S.L. — Sushigi Catering. Todos los derechos reservados.
            </Typography>
            <Box sx={{ display: 'flex', gap: 3 }}>
              {[
                { label: 'Política de privacidad', action: () => setPrivacyOpen(true) },
                { label: 'Aviso legal', action: () => setLegalOpen(true) },
              ].map(({ label, action }) => (
                <Typography
                  key={label}
                  component="button"
                  variant="caption"
                  onClick={action}
                  sx={{
                    color: 'var(--color-text-secondary)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    p: 0,
                    fontFamily: 'inherit',
                    fontSize: 'inherit',
                    transition: 'color 0.2s',
                    '&:hover': { color: 'var(--color-accent)' },
                  }}
                >
                  {label}
                </Typography>
              ))}
            </Box>
          </Box>
        </Container>
      </Box>

      <PrivacyModal open={privacyOpen} onClose={() => setPrivacyOpen(false)} />
      <LegalModal open={legalOpen} onClose={() => setLegalOpen(false)} />
    </>
  );
};

export default Footer;
